import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/InventoryWizard.css';

const InventoryWizard = ({ clientCode = 'CAAM', onComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    email: '',
    phoneExt: '',
    jobTitle: '',
    department: '',
    oneBuilding: '',
    buildingName: '',
    floor: '',
    officeNumber: ''
  });

  const [availableFurniture, setAvailableFurniture] = useState([]);
  const [furnitureByType, setFurnitureByType] = useState({});
  
  const [selectedFurniture, setSelectedFurniture] = useState({
    type: '',
    inventoryId: '',
    description: '',
    quantity: 1,
    condition: '',
    notes: ''
  });

  const [computerData, setComputerData] = useState(null);
  const [detectionStatus, setDetectionStatus] = useState('pending');
  const [detectionCode, setDetectionCode] = useState('');

  useEffect(() => {
    fetchAvailableFurniture();
  }, []);

  const fetchAvailableFurniture = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/inventory-items/available`, {
        params: { clientCode: 'caam', category: 'furniture' }
      });
      
      setAvailableFurniture(response.data);
      
      const grouped = response.data.reduce((acc, item) => {
        if (!acc[item.item_type]) {
          acc[item.item_type] = [];
        }
        acc[item.item_type].push(item);
        return acc;
      }, {});
      
      setFurnitureByType(grouped);
    } catch (error) {
      console.error('Error fetching available furniture:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFurnitureTypeChange = (e) => {
    const type = e.target.value;
    setSelectedFurniture({
      type,
      inventoryId: '',
      description: '',
      quantity: 1,
      condition: '',
      notes: ''
    });
  };

  const handleFurnitureDescriptionChange = (e) => {
    const inventoryId = e.target.value;
    const selectedItem = availableFurniture.find(item => item.id === parseInt(inventoryId));
    
    if (selectedItem) {
      setSelectedFurniture(prev => ({
        ...prev,
        inventoryId,
        description: selectedItem.description,
        condition: selectedItem.condition
      }));
    }
  };

  const handleSelectedFurnitureChange = (e) => {
    const { name, value } = e.target;
    setSelectedFurniture(prev => ({ ...prev, [name]: value }));
  };

  const handleDetectionCodeSubmit = () => {
    if (!detectionCode.trim()) {
      alert('Please enter the detection code from the agent window');
      return;
    }

    try {
      const decodedData = JSON.parse(atob(detectionCode));
      setComputerData(decodedData);
      setDetectionStatus('detected');
    } catch (error) {
      alert('Invalid detection code. Please copy the entire code from the agent window.');
    }
  };

  const handleSubmit = async () => {
    try {
      const submissionData = {
        clientCode,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneExt: formData.phoneExt,
        jobTitle: formData.jobTitle,
        department: formData.department,
        officeNumber: formData.officeNumber,
        buildingName: formData.buildingName,
        floor: formData.floor,
        furniture: selectedFurniture,
        computerData: computerData,
        username: formData.username,
        password: formData.password
      };

      await axios.post(`${process.env.REACT_APP_API_URL}/wizard/complete`, submissionData);
      
      setCurrentStep(5);
      if (onComplete) onComplete();
    } catch (error) {
      console.error('Error submitting equipment assignment:', error);
      alert('There was an error submitting your equipment assignment. Please try again.');
    }
  };

  const nextStep = () => {
    if (currentStep === 1) {
      if (!formData.username || !formData.password || !formData.confirmPassword) {
        alert('Please fill in username and password fields');
        return;
      }
      if (formData.password !== formData.confirmPassword) {
        alert('Passwords do not match!');
        return;
      }
      if (formData.password.length < 8) {
        alert('Password must be at least 8 characters long');
        return;
      }
      if (!formData.firstName || !formData.lastName || !formData.email) {
        alert('Please fill in all required personal information fields');
        return;
      }
    }
    
    setCurrentStep(prev => prev + 1);
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const steps = [
    { num: 1, label: 'Account & Info' },
    { num: 2, label: 'Furniture' },
    { num: 3, label: 'Computer' },
    { num: 4, label: 'Review' }
  ];

  const renderStepIndicator = () => (
    <div className="step-indicator">
      {steps.map(step => (
        <div key={step.num} className={`step ${currentStep >= step.num ? 'active' : ''} ${currentStep === step.num ? 'current' : ''}`}>
          <div className="step-circle">
            {currentStep > step.num ? (
              <svg className="step-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            ) : (
              step.num
            )}
          </div>
          <span className="step-label">{step.label}</span>
        </div>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="wizard-content">
      <h2 className="wizard-section-title">Create Your Account</h2>
      <p className="wizard-section-subtitle">Choose your username and password</p>

      <div className="form-grid">
        <div className="form-group full-width">
          <label className="form-label required">Username</label>
          <input
            type="text"
            name="username"
            value={formData.username}
            onChange={handleInputChange}
            className="form-input"
            placeholder="Choose a unique username"
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label required">Password</label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Choose a strong password"
              required
              minLength="8"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              {showPassword ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              )}
            </button>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label required">Confirm Password</label>
          <div style={{ position: 'relative' }}>
            <input
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Re-enter your password"
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              style={{
                position: 'absolute',
                right: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              {showConfirmPassword ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                  <circle cx="12" cy="12" r="3"/>
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      <h2 className="wizard-section-title" style={{ marginTop: '32px' }}>Personal Information</h2>
      <p className="wizard-section-subtitle">Tell us about yourself</p>

      <div className="form-grid">
        <div className="form-group">
          <label className="form-label required">First Name</label>
          <input
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            className="form-input"
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label required">Last Name</label>
          <input
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            className="form-input"
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label required">Email Address</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className="form-input"
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label">Phone Extension</label>
          <input
            type="text"
            name="phoneExt"
            value={formData.phoneExt}
            onChange={handleInputChange}
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label className="form-label required">Job Title</label>
          <input
            type="text"
            name="jobTitle"
            value={formData.jobTitle}
            onChange={handleInputChange}
            className="form-input"
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label required">Department</label>
          <select
            name="department"
            value={formData.department}
            onChange={handleInputChange}
            className="form-select"
            required
          >
            <option value="">Select department...</option>
            <option value="Administration">Administration</option>
            <option value="Collections">Collections</option>
            <option value="Exhibitions">Exhibitions</option>
            <option value="Education">Education</option>
            <option value="Development">Development</option>
            <option value="Communications">Communications</option>
            <option value="Operations">Operations</option>
            <option value="Finance">Finance</option>
          </select>
        </div>

        <div className="form-group full-width">
          <label className="form-label required">Office Location</label>
          <div className="radio-group">
            <label className="radio-option">
              <input
                type="radio"
                name="oneBuilding"
                value="yes"
                checked={formData.oneBuilding === 'yes'}
                onChange={handleInputChange}
              />
              <span>Single Location</span>
            </label>
            <label className="radio-option">
              <input
                type="radio"
                name="oneBuilding"
                value="no"
                checked={formData.oneBuilding === 'no'}
                onChange={handleInputChange}
              />
              <span>Multiple Locations</span>
            </label>
          </div>
        </div>

        {formData.oneBuilding === 'yes' && (
          <>
            <div className="form-group">
              <label className="form-label">Building Name</label>
              <input
                type="text"
                name="buildingName"
                value={formData.buildingName}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Floor</label>
              <input
                type="text"
                name="floor"
                value={formData.floor}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Office Number</label>
              <input
                type="text"
                name="officeNumber"
                value={formData.officeNumber}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="wizard-content">
      <h2 className="wizard-section-title">Furniture Selection</h2>
      <p className="wizard-section-subtitle">Select furniture from available inventory</p>

      <div className="form-grid">
        <div className="form-group">
          <label className="form-label required">Furniture Type</label>
          <select
            name="type"
            value={selectedFurniture.type}
            onChange={handleFurnitureTypeChange}
            className="form-select"
          >
            <option value="">Select type...</option>
            {Object.keys(furnitureByType).sort().map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label required">Description</label>
          <select
            name="inventoryId"
            value={selectedFurniture.inventoryId}
            onChange={handleFurnitureDescriptionChange}
            className="form-select"
            disabled={!selectedFurniture.type}
          >
            <option value="">Select description...</option>
            {selectedFurniture.type && furnitureByType[selectedFurniture.type]?.map(item => (
              <option key={item.id} value={item.id}>
                {item.description} (Available: {item.quantity})
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label required">Quantity</label>
          <input
            type="number"
            name="quantity"
            min="1"
            value={selectedFurniture.quantity}
            onChange={handleSelectedFurnitureChange}
            className="form-input"
          />
        </div>

        <div className="form-group">
          <label className="form-label required">Condition</label>
          <select
            name="condition"
            value={selectedFurniture.condition}
            onChange={handleSelectedFurnitureChange}
            className="form-select"
          >
            <option value="">Select condition...</option>
            <option value="New">New</option>
            <option value="Excellent">Excellent</option>
            <option value="Good">Good</option>
            <option value="Fair">Fair</option>
          </select>
        </div>

        <div className="form-group full-width">
          <label className="form-label">Notes (Optional)</label>
          <input
            type="text"
            name="notes"
            value={selectedFurniture.notes}
            onChange={handleSelectedFurnitureChange}
            className="form-input"
            placeholder="Any additional details..."
          />
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="wizard-content">
      <h2 className="wizard-section-title">Computer Detection</h2>
      <p className="wizard-section-subtitle">Link your assigned computer to your profile</p>

      {detectionStatus === 'pending' && (
        <div className="detection-container">
          <div className="detection-status">
            <p><strong>Step 1:</strong> Run the computer detection tool on your assigned computer</p>
            <p><strong>Step 2:</strong> Copy the detection code from the agent window</p>
            <p><strong>Step 3:</strong> Paste it below</p>
          </div>

          <div className="form-group detection-code-input">
            <label className="form-label">Detection Code</label>
            <textarea
              value={detectionCode}
              onChange={(e) => setDetectionCode(e.target.value)}
              className="form-textarea"
              placeholder="Paste your detection code here..."
              rows="4"
            />
          </div>

          <button onClick={handleDetectionCodeSubmit} className="btn-submit-code">
            Submit Detection Code
          </button>

          <button onClick={() => setDetectionStatus('skipped')} className="btn-skip">
            Skip for now
          </button>
        </div>
      )}

      {detectionStatus === 'detected' && computerData && (
        <div className="computer-info">
          <h4 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 600 }}>Computer Detected Successfully</h4>
          <div className="computer-info-grid">
            <div className="computer-info-item">
              <span className="computer-info-label">Manufacturer</span>
              <span className="computer-info-value">{computerData.manufacturer || 'N/A'}</span>
            </div>
            <div className="computer-info-item">
              <span className="computer-info-label">Model</span>
              <span className="computer-info-value">{computerData.model || 'N/A'}</span>
            </div>
            <div className="computer-info-item">
              <span className="computer-info-label">Serial Number</span>
              <span className="computer-info-value">{computerData.serialNumber || 'N/A'}</span>
            </div>
            <div className="computer-info-item">
              <span className="computer-info-label">Hostname</span>
              <span className="computer-info-value">{computerData.hostname || 'N/A'}</span>
            </div>
            <div className="computer-info-item">
              <span className="computer-info-label">Operating System</span>
              <span className="computer-info-value">{computerData.os || 'N/A'}</span>
            </div>
            <div className="computer-info-item">
              <span className="computer-info-label">RAM</span>
              <span className="computer-info-value">{computerData.ram || 'N/A'}</span>
            </div>
          </div>
        </div>
      )}

      {detectionStatus === 'skipped' && (
        <div className="detection-container">
          <p style={{ color: '#737373', margin: 0 }}>Computer detection skipped. You can update this information later.</p>
        </div>
      )}
    </div>
  );

  const renderStep4 = () => (
    <div className="wizard-content">
      <h2 className="wizard-section-title">Review & Submit</h2>
      <p className="wizard-section-subtitle">Please review your information before submitting</p>

      <div className="review-section">
        <div className="review-section-title">Account Information</div>
        <div className="review-grid">
          <div className="review-item">
            <span className="review-label">Username</span>
            <span className="review-value">{formData.username}</span>
          </div>
        </div>
      </div>

      <div className="review-section">
        <div className="review-section-title">Personal Information</div>
        <div className="review-grid">
          <div className="review-item">
            <span className="review-label">Name</span>
            <span className="review-value">{formData.firstName} {formData.lastName}</span>
          </div>
          <div className="review-item">
            <span className="review-label">Email</span>
            <span className="review-value">{formData.email}</span>
          </div>
          <div className="review-item">
            <span className="review-label">Job Title</span>
            <span className="review-value">{formData.jobTitle}</span>
          </div>
          <div className="review-item">
            <span className="review-label">Department</span>
            <span className="review-value">{formData.department}</span>
          </div>
        </div>
      </div>

      <div className="review-section">
        <div className="review-section-title">Furniture</div>
        {selectedFurniture.inventoryId ? (
          <div style={{ fontSize: '14px', color: '#000000' }}>
            • {selectedFurniture.type} - {selectedFurniture.description} (Qty: {selectedFurniture.quantity}, {selectedFurniture.condition})
          </div>
        ) : (
          <p style={{ color: '#737373', fontSize: '13px', margin: 0 }}>No furniture selected</p>
        )}
      </div>

      <div className="review-section">
        <div className="review-section-title">Computer</div>
        {computerData ? (
          <div className="review-grid">
            <div className="review-item">
              <span className="review-label">Model</span>
              <span className="review-value">{computerData.manufacturer} {computerData.model}</span>
            </div>
            <div className="review-item">
              <span className="review-label">Serial Number</span>
              <span className="review-value">{computerData.serialNumber}</span>
            </div>
          </div>
        ) : (
          <p style={{ color: '#737373', fontSize: '13px', margin: 0 }}>No computer detected</p>
        )}
      </div>
    </div>
  );

  const renderSuccess = () => (
    <div className="success-container">
      <div className="success-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
      </div>
      <h2 className="success-title">Equipment Assignment Complete!</h2>
      <p className="success-message">
        Your information has been submitted successfully.
      </p>
    </div>
  );

  if (currentStep === 5) {
    return (
      <div className="wizard-container">
        <div className="wizard-card">
          <div className="wizard-header">
            <img src="/caam-logo.png" alt="CAAM" className="wizard-logo" />
          </div>
          {renderSuccess()}
        </div>
      </div>
    );
  }

  return (
    <div className="wizard-container">
      <div className="wizard-card">
        <div className="wizard-header">
          <img src="/caam-logo.png" alt="CAAM" className="wizard-logo" />
          <h1 className="wizard-title">Equipment Assignment</h1>
          <p className="wizard-subtitle">Complete your inventory assignment</p>
        </div>

        {renderStepIndicator()}

        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}

        <div className="wizard-actions">
          {currentStep > 1 && currentStep < 5 && (
            <button onClick={prevStep} className="btn-secondary">
              ← Back
            </button>
          )}
          
          <div style={{ flex: 1 }}></div>

          {currentStep < 4 && (
            <button onClick={nextStep} className="btn-primary">
              Continue →
            </button>
          )}

          {currentStep === 4 && (
            <button onClick={handleSubmit} className="btn-primary">
              Submit Equipment Assignment
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InventoryWizard;